import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { UserPlusIcon } from "lucide-react";

import { InvitationOfType, UseInvitationOptions } from "simplydo/interfaces";

import {
  acceptAuthenticatedInvitation,
  rejectAuthenticatedInvitation,
  removeAuthenticatedInvitation,
  toastError,
  toastMessage,
  toastSuccess,
  withInvitationPrompt,
} from "./internal";
import type { InvitationViewModel } from "./types";
import { emptyInvitationViewModel } from "./types";

export const useProjectInvitedInvitation = (
  invitation: InvitationOfType<"projectInvited"> | undefined,
  options?: UseInvitationOptions,
): InvitationViewModel => {
  const { t } = useTranslation();

  const idea = invitation?.idea;
  const ideaId = idea?._id;
  const ideaName = idea?.name || t("invitations.deletedIdea");
  const inviterName = invitation?.inviterUser?.profile?.fullName;
  const inviteeName =
    invitation?.inviteeUser?.profile?.fullName || invitation?.invitee;
  const canAccept = !!invitation?.canAccept && !!idea;
  const createdByCurrentUser = !!invitation?.createdByCurrentUser;

  const acceptInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    acceptAuthenticatedInvitation(
      invitation,
      () => {
        toastSuccess(
          t("invitations.projectInvited.accepted", { idea: t("generic.idea") }),
        );
      },
      () =>
        toastError(
          t("invitations.projectInvited.acceptError", {
            idea: t("generic.idea"),
          }),
        ),
    );
  }, [invitation, t]);

  const rejectInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.projectInvited.rejectTitle"),
      t("invitations.projectInvited.rejectMessage", {
        inviterName,
        ideaName,
        idea: t("generic.idea"),
      }),
      () => {
        rejectAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.rejected")),
          () => toastError(t("invitations.rejectError")),
        );
      },
    );
  }, [ideaName, invitation, inviterName, options, t]);

  const removeInvite = useCallback(() => {
    if (!invitation) {
      return;
    }
    withInvitationPrompt(
      options,
      t("invitations.projectInvited.removeTitle"),
      t("invitations.projectInvited.removeMessage", {
        inviteeName,
        ideaName,
        idea: t("generic.idea"),
      }),
      () => {
        removeAuthenticatedInvitation(
          invitation,
          () => toastMessage(t("invitations.removed")),
          () => toastError(t("invitations.removeError")),
        );
      },
    );
  }, [ideaName, invitation, inviteeName, options, t]);

  return useMemo(() => {
    if (!invitation) {
      return emptyInvitationViewModel();
    }

    const boardLink =
      idea?.challenge && ideaId
        ? `/challenges/${idea.challenge}/board?idea=${ideaId}`
        : null;

    return {
      icon: UserPlusIcon,
      link: boardLink ?? (ideaId ? `/ideas/${ideaId}` : null),
      isInvalid: !idea,
      text: {
        title: ideaName,
        fullTitle: t("invitations.projectInvited.fullPageTitle", {
          name: ideaName,
          idea: t("generic.idea"),
        }),
        youInvited: t("invitations.projectInvited.creatorPostText", {
          inviteeName,
          idea: t("generic.idea"),
        }),
        hasInvitedYou: t("invitations.projectInvited.receivedPostText", {
          idea: t("generic.idea"),
        }),
        dismiss: createdByCurrentUser
          ? t("invitations.remove")
          : t("invitations.reject"),
        invalid: t("invitations.projectInvited.invalidMessage", {
          idea: t("generic.idea"),
        }),
        ...(boardLink || ideaId
          ? { goTo: t("invitations.projectInvited.view") }
          : {}),
      },
      acceptInvite: canAccept ? acceptInvite : undefined,
      rejectInvite: !createdByCurrentUser ? rejectInvite : undefined,
      removeInvite: createdByCurrentUser ? removeInvite : undefined,
    };
  }, [
    acceptInvite,
    canAccept,
    createdByCurrentUser,
    idea,
    ideaId,
    ideaName,
    invitation,
    inviteeName,
    removeInvite,
    rejectInvite,
    t,
  ]);
};
